/**
 * Funciones puras de formato de hora (sin DI, sin efectos secundarios).
 * Extraídas de dialog-anuncio-informativo.component.ts para reutilizarse también
 * en dialog-work-schedule-calendar.component.ts sin duplicar lógica.
 */

export function timeToMinutes(time: string): number | null {
  if (!time) { return null; }
  const m = time.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) { return null; }
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

export function minutesToHHmm(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Extrae minutos de una cadena de hora "suelta" proveniente de la API (p. ej. "07:00:00"
 * o "7:00"), a diferencia de timeToMinutes que exige el formato exacto "HH:mm".
 */
export function extractMinutesFromApiTime(val: string): number {
  if (!val) { return 0; }
  const m = val.match(/(\d{1,2}):(\d{2})/);
  if (!m) { return 0; }
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

/** Normaliza una cadena de hora "suelta" de la API al formato canónico "HH:mm". */
export function extractHHmm(val: string): string {
  if (!val) { return ''; }
  const m = val.match(/(\d{1,2}):(\d{2})/);
  if (!m) { return val; }
  return `${m[1].padStart(2, '0')}:${m[2]}`;
}

/** Rango legible a partir de dos horas "sueltas" de la API, ej. "07:00 – 16:00". */
export function formatApiTimeRange(startHour: string | null, endHour: string | null): string {
  if (!startHour || !endHour) { return ''; }
  return `${extractHHmm(startHour)} – ${extractHHmm(endHour)}`;
}

/** Algunos turnos modelan un día sin jornada como "00:00 a 00:00" en vez de simplemente no traer
 * horario ese día (ej. un turno nocturno puede traer Domingo/Sábado así, con una duración
 * calculada negativa del lado de la API). Sin este chequeo, ese día se contaba como una jornada
 * real de 0h (o incluso positiva, si algo aguas arriba interpreta mal el signo negativo) en vez
 * de descanso, y el total de horas terminaba dependiendo de qué tan bien cada fuente calculara
 * esas horas "fantasma". Usado tanto en "Programación masiva de horarios" como en "Programación
 * de horarios" (individual) para que un mismo turno sume igual en las dos pantallas. */
export function isZeroDurationRange(startHour: string | null | undefined, endHour: string | null | undefined): boolean {
  if (!startHour || !endHour) { return true; }
  return extractHHmm(startHour) === extractHHmm(endHour);
}

/** Formatea un número de horas con coma decimal (convención es-ES), ej. 42.5 → "42,5". */
export function formatHoursEs(hours: number): string {
  return String(hours).replace('.', ',');
}

export function toShortTime12h(val: string): string {
  if (!val) { return '—'; }
  const m = val.match(/(\d{1,2}):(\d{2})/);
  if (!m) { return val; }
  let h = parseInt(m[1], 10);
  const min = m[2];
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${String(h).padStart(2, '0')}:${min} ${period}`;
}
